import {Action, Selector, State, StateContext} from '@ngxs/store';
import {tap} from 'rxjs/operators';
import {FordocUserService} from '@app/services/fordoc-user.service';
import {FordocUser} from '@app/models/form/fordoc-user.model';
import {Session} from '@app/app.session';

export class FordocUserStateModel {
  all: FordocUser[];
  selected: FordocUser;
  current: FordocUser;
  sponsors: FordocUser[];
  ulgMembers: FordocUser[];
  uclMembers: FordocUser[];
  ulbMembers: FordocUser[];
  unrMembers: FordocUser[];
}

export class GetFordocUser {
  static readonly type = '[fordocUsers] GetFordocUser';

  constructor(public uuid: string) {
  }
}

export class GetAllFordocUser {
  static readonly type = '[fordocUsers] GetAllFordocUser';
}

export class GetCurrent {
  static readonly type = '[fordocUsers] GetCurrent';
}

export class GetSponsors {
  static readonly type = '[fordocUsers] GetSponsors';
}

export class GetUlgMembers {
  static readonly type = '[fordocUsers] GetUlgMembers';
}

export class GetUclMembers {
  static readonly type = '[fordocUsers] GetUclMembers';
}

export class GetUlbMembers {
  static readonly type = '[fordocUsers] GetUlbMembers';
}

export class GetUnrMembers {
  static readonly type = '[fordocUsers] GetUnrMembers';
}

@State<FordocUserStateModel>({
  name: 'fordocUsers',
  defaults: {
    all: [],
    current: new FordocUser(),
    selected: new FordocUser(),
    sponsors: [],
    ulgMembers: [],
    uclMembers: [],
    ulbMembers: [],
    unrMembers: []
  }
})
export class FordocUserState {

  private static fordocUserService: FordocUserService;

  sponsorCache: FordocUser[];

  constructor(private fordocUserService: FordocUserService, private session: Session) {
    FordocUserState.fordocUserService = fordocUserService;
  }

  @Selector()
  static all(state: FordocUserStateModel) {
    return state.all;
  }

  @Selector()
  static selected(state: FordocUserStateModel) {
    return state.selected;
  }

  @Selector()
  static current(state: FordocUserStateModel) {
    return state.current;
  }

  @Selector()
  static sponsors(state: FordocUserStateModel) {
    return state.sponsors;
  }

  @Selector()
  static ulgMembers(state: FordocUserStateModel) {
    return state.ulgMembers;
  }

  @Selector()
  static uclMembers(state: FordocUserStateModel) {
    return state.uclMembers;
  }

  @Selector()
  static ulbMembers(state: FordocUserStateModel) {
    return state.ulbMembers;
  }

  @Selector()
  static unrMembers(state: FordocUserStateModel) {
    return state.unrMembers;
  }

  @Action(GetFordocUser)
  getOne(ctx: StateContext<FordocUserStateModel>, action: GetFordocUser) {
    return this.fordocUserService.get(action.uuid).pipe(
      tap(res => {
        ctx.patchState({selected: res});
      })
    );
  }

  @Action(GetSponsors)
  getSponsors(ctx: StateContext<FordocUserStateModel>, action: GetSponsors) {
    ctx.patchState({sponsors: []}); // empty sponsors until response arrives
    return this.fordocUserService.getSponsors().pipe(
      tap(res => {
        ctx.patchState({sponsors: res});
      })
    );
  }

  @Action(GetAllFordocUser)
  save(ctx: StateContext<FordocUserStateModel>, action: GetAllFordocUser) {
    return this.fordocUserService.getAll().pipe(
      tap(res => {
        ctx.patchState({all: res});
      })
    );
  }

  @Action(GetCurrent)
  getCurrent(ctx: StateContext<FordocUserStateModel>, action: GetCurrent) {
    ctx.patchState({current: this.session.user});
  }

  @Action(GetUlgMembers)
  getUlgMembers(ctx: StateContext<FordocUserStateModel>, action: GetUlgMembers) {
    return this.fordocUserService.getUlgMembers().pipe(
      tap(res => {
        ctx.patchState({ulgMembers: res});
      })
    );
  }

  @Action(GetUclMembers)
  getUclMembers(ctx: StateContext<FordocUserStateModel>, action: GetUclMembers) {
    return this.fordocUserService.getUclMembers().pipe(
      tap(res => {
        ctx.patchState({uclMembers: res});
      })
    );
  }

  @Action(GetUlbMembers)
  getUlbMembers(ctx: StateContext<FordocUserStateModel>, action: GetUlbMembers) {
    return this.fordocUserService.getUlbMembers().pipe(
      tap(res => {
        ctx.patchState({ulbMembers: res});
      })
    );
  }

  @Action(GetUnrMembers)
  getUnrMembers(ctx: StateContext<FordocUserStateModel>, action: GetUnrMembers) {
    return this.fordocUserService.getUnrMembers().pipe(
      tap(res => {
        ctx.patchState({unrMembers: res});
      })
    );
  }

}
